<template>
  <div class="p-3 pb-24 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <div class="bg-gray-900 bg-opacity-80 border border-gray-800 rounded-md mb-3 shadow-xl px-4 py-3">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <i class="fas fa-chess-rook text-indigo-400 text-lg"></i>
          <h1 class="text-lg font-semibold text-indigo-300 leading-tight">ระบบยึดครองแผนที่</h1>
        </div>
        <!-- Quick status (compact) -->
        <div class="hidden sm:flex items-center gap-3 text-xxs">
          <div class="px-2 py-1 bg-gray-800 border border-gray-700 rounded">แผนที่: <span class="text-indigo-300 font-semibold">{{ rankedMaps.length }}</span></div>
          <div class="px-2 py-1 bg-gray-800 border border-gray-700 rounded">ผู้เล่นติดอันดับ: <span class="text-indigo-300 font-semibold">{{ occupiers.length }}</span></div>
          <div class="px-2 py-1 bg-gray-800 border border-gray-700 rounded">รางวัล: <span class="text-indigo-300 font-semibold">{{ sortedTiers.length }}</span></div>
        </div>
      </div>
    </div>

    <!-- Tabs (reordered: maps -> players -> rewards) -->
    <div class="flex border-b border-gray-700 bg-gray-700 mb-4">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        @click="activeTab = tab.id"
        class="flex-1 px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2"
        :class="activeTab === tab.id
          ? 'text-indigo-300 border-b-2 border-indigo-600 bg-gray-800'
          : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-600'"
      >
        <span>{{ tab.name }}</span>
        <span v-if="tab.badge !== null" class="text-xxs px-1.5 py-0.5 rounded bg-gray-800 border border-gray-600">
          {{ tab.badge }}
        </span>
      </button>
    </div>

    <!-- Tab: Maps -->
    <div v-if="activeTab === 'maps'">
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left text-gray-400">
          <thead class="text-xs uppercase bg-gray-700 text-gray-300 sticky top-0 z-10">
            <tr>
              <th scope="col" class="px-6 py-3">แผนที่</th>
              <th scope="col" class="px-6 py-3">ผู้ยึดครอง</th>
              <th scope="col" class="px-6 py-3">คะแนนแผนที่</th>
            </tr>
          </thead>
          <tbody>
            <!-- Loading skeleton -->
            <tr v-if="isLoadingMaps">
              <td colspan="4" class="px-6 py-6">
                <div class="flex justify-center py-4">
                  <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-indigo-600"></div>
                </div>
              </td>
            </tr>

            <tr
              v-for="(map) in rankedMaps"
              :key="map.id"
              class="bg-gray-800 border-b border-gray-700 hover:bg-gray-700 transition-colors"
            >
              <td class="px-6 py-4">
                <div class="flex items-center gap-3">
                  <img
                    :src="map.imageUrl || fallbackImg"
                    alt="Map Image"
                    class="w-10 h-10 rounded-md object-cover border border-gray-600"
                    @error="handleImageError"
                  />
                  <i class="fa-solid" :class="map.treasureOpened ? 'fa-box-open text-red-500' : 'fa-box text-green-500'"></i>
                  <span class="text-indigo-200">{{ map.name }}</span>
                </div>
              </td>
              <td class="px-6 py-4">
                <div v-if="map.occupierUid" class="flex items-center gap-3">
                  <img
                    :src="map.occupier.imageUrl || fallbackImg"
                    alt="Occupier"
                    class="hidden sm:block w-8 h-8 rounded-full object-cover border border-indigo-400"
                    @error="handleImageError"
                  />
                  <span class="text-indigo-200">{{ map.occupier.name }}</span>
                </div>
                <span v-else class="text-gray-400">ไม่มีผู้ยึดครอง</span>
              </td>
              <td class="px-6 py-4">{{ map.occupationScore || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty -->
      <div v-if="rankedMaps.length === 0 && !isLoadingMaps" class="text-center text-indigo-200 py-10 text-sm">
        ไม่พบแผนที่ที่เปิดให้ยึดครอง
      </div>
    </div>

    <!-- Tab: Players -->
    <div v-if="activeTab === 'players'">
      <!-- Current Player -->
      <div v-if="currentPlayer" class="bg-gray-900 bg-opacity-70 rounded-md p-4 mb-4 border border-indigo-400">
        <div class="flex items-center gap-4">
          <div class="relative">
            <img :src="currentPlayer.imageUrl || fallbackImg" class="w-12 h-12 rounded-md object-cover border-2 border-indigo-400" @error="handleImageError" />
            <div class="absolute -bottom-1 -right-1 bg-indigo-600 text-white text-xxs font-semibold rounded-full w-5 h-5 flex items-center justify-center border border-gray-600">
              {{ currentPlayer.level || 'N/A' }}
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <h3 class="text-indigo-200 text-sm font-semibold truncate">{{ currentPlayer.characterName || 'ไม่ทราบ' }}</h3>
              <span v-if="currentPlayerRank" class="inline-flex items-center px-2 py-0.5 rounded bg-indigo-700 border border-indigo-400 text-xxs">
                อันดับ {{ currentPlayerRank }}
              </span>
              <span v-else class="inline-flex items-center px-2 py-0.5 rounded bg-gray-700 border border-gray-400 text-xxs">
                ไม่ติดอันดับ
              </span>
            </div>
            <div class="flex flex-wrap gap-x-4 gap-y-1 text-xxs text-indigo-200 opacity-90 mt-0.5">
              <span><i class="fas fa-star mr-1"></i>คะแนนยึดครอง: {{ currentPlayer.occupationScore || 0 }}</span>
            </div>
          </div>
          <div v-if="isAdmin" class="ml-auto">
            <button
              @click="openCharacterView(currentPlayer.docId)"
              class="text-indigo-200 hover:text-indigo-400 transition-colors"
              title="ดูข้อมูลตัวละคร"
            >
              <i class="fas fa-eye"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading / Error -->
      <div v-if="isLoadingPlayers && !occupiers.length" class="space-y-2">
        <div v-for="n in 6" :key="n" class="bg-gray-900 bg-opacity-70 rounded-md p-3 border border-gray-800 animate-pulse">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-full bg-gray-700 border border-gray-600"></div>
            <div class="w-12 h-12 rounded-md bg-gray-700 border-2 border-gray-600"></div>
            <div class="flex-1">
              <div class="h-3 bg-gray-700 rounded mb-2 w-2/3"></div>
              <div class="h-2 bg-gray-700 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="errorPlayers" class="text-center text-red-300 py-8 text-sm">
        {{ errorPlayers }}
      </div>

      <!-- Players list -->
      <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <article
          v-for="(occupier, index) in occupiers"
          :key="occupier.docId"
          class="bg-gray-900 bg-opacity-70 rounded-md p-3 border transition-all duration-150"
          :class="cardClass(index, occupier)"
        >
          <div class="flex items-center gap-3">
            <!-- Rank bubble -->
            <div class="w-9 h-9 flex items-center justify-center rounded-full text-white text-sm font-semibold border"
                 :class="rankBubbleClass(index)">
              #{{ index + 1 }}
            </div>

            <!-- Avatar -->
            <div class="relative">
              <img
                :src="occupier.imageUrl || fallbackImg"
                alt="ผู้ยึดครอง"
                class="w-12 h-12 rounded-md object-cover border-2 border-indigo-400"
                @error="handleImageError"
              />
              <div class="absolute -bottom-1 -right-1 bg-indigo-600 text-white text-xxs font-semibold rounded-full w-5 h-5 flex items-center justify-center border border-gray-600">
                {{ occupier.level || 'N/A' }}
              </div>
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1">
                <h3 class="text-indigo-200 text-sm font-semibold truncate">{{ occupier.characterName || 'ไม่ทราบ' }}</h3>
                <i v-if="occupier.docId === userId" class="fas fa-user-shield text-indigo-400 text-xs ml-1" title="นี่คือตัวคุณ"></i>
              </div>
              <div class="grid grid-cols-1 gap-1 text-xxs text-indigo-200 opacity-90">
                <span><i class="fas fa-star mr-1"></i>คะแนนยึดครอง: {{ occupier.occupationScore || 0 }}</span>
              </div>
            </div>

            <!-- Admin eye -->
            <button
              v-if="isAdmin"
              @click="openCharacterView(occupier.docId)"
              class="text-indigo-200 hover:text-indigo-400 transition-colors ml-auto"
              title="ดูข้อมูลตัวละคร"
            >
              <i class="fas fa-eye"></i>
            </button>
          </div>
        </article>
      </div>

      <!-- Empty -->
      <div v-if="occupiers.length === 0 && !isLoadingPlayers" class="text-center text-indigo-200 py-10 text-sm">
        ไม่พบผู้ยึดครองที่มีคะแนนยึดครอง
      </div>

      <!-- Load more -->
      <div v-if="(isAdmin || occupiers.length < 75) && hasMorePlayers && !isLoadingMorePlayers" class="text-center mt-5">
        <button
          @click="loadMoreOccupiers"
          class="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 border border-indigo-400 transition-colors"
        >
          โหลดเพิ่ม
        </button>
      </div>
      <div v-if="isLoadingMorePlayers" class="flex flex-col items-center py-5">
        <div class="animate-spin rounded-full h-8 w-8 border-t-4 border-indigo-600 mb-2"></div>
        <p class="text-indigo-200 text-xs">กำลังโหลดข้อมูลเพิ่ม...</p>
      </div>
      <div v-if="!hasMorePlayers && occupiers.length > 0" class="text-center text-indigo-200 text-xs mt-4">
        ไม่มีข้อมูลเพิ่มเติม
      </div>
    </div>

    <!-- Tab: Rewards -->
    <div v-if="activeTab === 'rewards'">
      <div class="text-center text-indigo-200 text-sm mb-4 animate-fadeIn">
        รางวัลจะมอบในอีก {{ timeLeft }}
      </div>

      <div v-if="isLoadingTiers" class="text-center text-indigo-200 text-sm animate-fadeIn">
        กำลังโหลดข้อมูลรางวัล...
      </div>

      <div v-else-if="sortedTiers.length > 0" class="space-y-3 animate-fadeIn scrollbar-thin">
        <div
          v-for="(tier, index) in sortedTiers"
          :key="index"
          class="flex items-center gap-3 p-3 bg-gray-900 rounded-md cursor-pointer hover:bg-gray-800 transition-all duration-200"
          @click="showItemDetails(tier.itemName)"
        >
          <div class="flex-1">
            <p class="text-indigo-200 font-medium text-sm">
              อันดับ {{ tier.min }}{{ tier.min !== tier.max ? `-${tier.max}` : '' }}
            </p>
            <div class="flex items-center gap-2 text-indigo-300 text-xs">
              <span>ไอเทม: {{ tier.itemName }}</span>
            </div>
          </div>
          <img
            v-if="tier.img"
            :src="tier.img"
            alt="ไอเทมรางวัล"
            class="w-10 h-10 object-cover rounded-full border border-gray-600"
          />
        </div>
      </div>

      <div v-else class="text-center text-red-300 text-sm animate-fadeIn">
        ไม่พบข้อมูลรางวัล
      </div>

      <!-- Item Details Modal -->
      <div
        v-if="selectedItem"
        class="fixed inset-0 z-60 flex items-center justify-center bg-gray-900 bg-opacity-70 animate-modalFade"
        @click.self="selectedItem = null"
      >
        <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn relative">
          <div class="flex justify-between items-center mb-3">
            <h3 class="text-base font-semibold text-indigo-300">{{ selectedItem }}</h3>
            <button @click="selectedItem = null" class="text-gray-300 hover:text-indigo-300 group relative">
              <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
              <span class="tooltip">ปิด</span>
            </button>
          </div>
          <div class="text-indigo-200 text-sm space-y-2">
            <div class="flex items-center gap-3">
              <img
                v-if="currentItemDetails.img"
                :src="currentItemDetails.img"
                :alt="selectedItem"
                class="w-10 h-10 object-cover rounded-full border border-gray-600"
              />
              <div>
                <div><span class="font-medium">ประเภท:</span> {{ currentItemDetails.type || 'N/A' }}</div>
              </div>
            </div>
            <div><span class="font-medium">เลเวลขั้นต่ำ:</span> {{ currentItemDetails.requiredLevel || 1 }}</div>
            <div><span class="font-medium">น้ำหนัก:</span> {{ currentItemDetails.weight || 0 }}</div>
            <div><span class="font-medium">สวมใส่ได้:</span> {{ wearableTypes.includes(currentItemDetails.type) ? 'ใช่' : 'ไม่' }}</div>
            <div class="flex items-center gap-1">
              <span class="font-medium">ราคา:</span>
              <i v-if="currentItemDetails.currencyType === 'gold'" class="fa-solid fa-g text-yellow-400"></i>
              <i v-else-if="currentItemDetails.currencyType === 'diamond'" class="fa-solid fa-gem text-blue-400"></i>
              <i v-else class="fa-solid fa-coins text-gray-100"></i>
              {{ currentItemDetails.price || 0 }}
            </div>
            <div><span class="font-medium">คำอธิบาย:</span> {{ currentItemDetails.description || 'ไม่มี' }}</div>

            <div v-if="hasStats">
              <span class="font-medium">Stats:</span>
              <ul class="list-disc ml-4 text-xs">
                <li v-if="itemStats.stamina > 0">Stamina: {{ itemStats.stamina }}</li>
                <li v-if="itemStats.str > 0">Strength: {{ itemStats.str }}</li>
                <li v-if="itemStats.int > 0">Intelligence: {{ itemStats.int }}</li>
                <li v-if="itemStats.agi > 0">Agility: {{ itemStats.agi }}</li>
                <li v-if="itemStats.maxHp > 0">Max HP: {{ itemStats.maxHp }}</li>
                <li v-if="itemStats.atk > 0">Attack: {{ itemStats.atk }}</li>
                <li v-if="itemStats.evasion > 0">Evasion: {{ itemStats.evasion }}</li>
                <li v-if="itemStats.critical > 0">Critical: {{ itemStats.critical }}</li>
                <li v-if="itemStats.defense > 0">Defense: {{ itemStats.defense }}</li>
              </ul>
            </div>

            <div v-if="currentItemDetails.type === 'รักษา' && currentItemDetails.healHpPercentage > 0">
              <span class="font-medium">HP ที่ได้รับ:</span> {{ currentItemDetails.healHpPercentage }}%
            </div>
          </div>
        </div>
      </div>
    </div> <!-- /rewards -->
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { projectFirestore, projectAuth } from '@/firebase/config';
import { useRouter } from 'vue-router';
import { usePlayerStore } from '@/composables/usePlayerStore';
import gameItemsData from '@/data/gameItems.json';

export default {
  name: 'OccupationRankingPage',
  setup() {
    const fallbackImg = 'https://via.placeholder.com/64';
    const handleImageError = (e) => { e.target.src = fallbackImg; };

    // Core state
    const activeTab = ref('maps'); // default -> แท็บแผนที่
    const userId = ref(null);
    const isAdmin = ref(false);
    const router = useRouter();

    // Players
    const occupiers = ref([]);
    const currentPlayer = ref(null);
    const isLoadingPlayers = ref(true);
    const isLoadingMorePlayers = ref(false);
    const errorPlayers = ref(null);
    const lastDocPlayer = ref(null);
    const hasMorePlayers = ref(true);
    const pageSize = 25;
    let playerListener = null;
    const playerStore = usePlayerStore();

    // Maps
    const rankedMaps = ref([]);
    const isLoadingMaps = ref(true);
    const errorMaps = ref(null);
    let mapListener = null;

    // Rewards
    const weeklyTiersOccupation = ref([]);
    const isLoadingRewards = ref(true);
    const errorRewards = ref(null);
    let rewardListener = null;

    // Item details cache
    const itemDetails = ref({});

    // Tabs with badges
    const tabs = computed(() => ([
      { id: 'maps', name: 'รายชื่อแผนที่', badge: rankedMaps.value.length ?? 0 },
      { id: 'players', name: 'อันดับผู้เล่น', badge: occupiers.value.length ?? 0 },
      { id: 'rewards', name: 'รางวัล', badge: sortedTiers.value.length ?? 0 }
    ]));

    const currentPlayerRank = computed(() => {
      if (!userId.value || !currentPlayer.value) return null;
      const index = occupiers.value.findIndex(p => p.docId === userId.value);
      return index !== -1 ? index + 1 : null;
    });

    const fetchUserId = async () => {
      try {
        const user = projectAuth.currentUser;
        if (user) {
          userId.value = user.uid;
          playerStore.init(user.uid);
        } else {
          errorPlayers.value = 'กรุณาล็อกอินเพื่อดูข้อมูล';
        }
      } catch (err) {
        errorPlayers.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลผู้ใช้: ' + err.message;
      }
    };

    watch(() => playerStore.userData, (data) => {
      if (data) {
        isAdmin.value = data.admin === true;
      }
    }, { immediate: true });

    watch(() => playerStore.characterData, (data) => {
      if (data && userId.value) {
        currentPlayer.value = { docId: userId.value, ...data };
      } else {
        currentPlayer.value = null;
      }
    }, { immediate: true });

    const listenToPlayers = () => {
      isLoadingPlayers.value = true;
      const query = projectFirestore
        .collection('characters')
        .where('isLive', '==', true)
        .where('occupationScore', '>', 0)
        .orderBy('occupationScore', 'desc')
        .orderBy('updatedAt', 'asc')
        .limit(pageSize);

      playerListener = query.onSnapshot(snapshot => {
        occupiers.value = snapshot.docs.map(d => ({ docId: d.id, ...d.data() }));
        lastDocPlayer.value = snapshot.docs.length ? snapshot.docs[snapshot.docs.length - 1] : null;
        hasMorePlayers.value = snapshot.docs.length === pageSize;
        isLoadingPlayers.value = false;
      }, err => {
        errorPlayers.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message;
        isLoadingPlayers.value = false;
      });
    };

    const loadMoreOccupiers = async () => {
      if (!hasMorePlayers.value || isLoadingMorePlayers.value || !lastDocPlayer.value) return;
      try {
        isLoadingMorePlayers.value = true;
        const snapshot = await projectFirestore
          .collection('characters')
          .where('isLive', '==', true)
          .where('occupationScore', '>', 0)
          .orderBy('occupationScore', 'desc')
          .startAfter(lastDocPlayer.value)
          .limit(pageSize)
          .get();
        const more = snapshot.docs.map(d => ({ docId: d.id, ...d.data() }));
        occupiers.value = [...occupiers.value, ...more];
        lastDocPlayer.value = snapshot.docs.length ? snapshot.docs[snapshot.docs.length - 1] : null;
        hasMorePlayers.value = snapshot.docs.length === pageSize;
      } catch (err) {
        errorPlayers.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลเพิ่ม: ' + err.message;
      } finally {
        isLoadingMorePlayers.value = false;
      }
    };

    const listenToMaps = () => {
      isLoadingMaps.value = true;
      const query = projectFirestore
        .collection('maps')
        .where('isActive','==',true)
        .where('isOccupationAllowed', '==', true)
        .orderBy('occupationScore', 'desc')

      mapListener = query.onSnapshot(async (snapshot) => {
        const maps = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const occupierUids = maps.map(m => m.occupierUid).filter(uid => uid);
        const uniqueUids = [...new Set(occupierUids)];

        const occupierDocs = await Promise.all(
          uniqueUids.map(uid => projectFirestore.collection('characters').doc(uid).get())
        );

        const occMap = {};
        occupierDocs.forEach(doc => {
          if (doc.exists) {
            occMap[doc.id] = {
              name: doc.data().characterName || 'N/A',
              imageUrl: doc.data().imageUrl || fallbackImg
            };
          }
        });

        rankedMaps.value = maps.map(m => ({
          ...m,
          occupier: m.occupierUid ? occMap[m.occupierUid] : null
        }));
        isLoadingMaps.value = false;
      }, err => {
        errorMaps.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message;
        isLoadingMaps.value = false;
      });
    };

    const listenToRewards = () => {
      isLoadingRewards.value = true;
      rewardListener = projectFirestore
        .collection('server')
        .doc('weeklyRewardsOccupation')
        .onSnapshot((doc) => {
          if (doc.exists) {
            weeklyTiersOccupation.value = doc.data().tiers || [];
            const uniqueItemNames = [...new Set(weeklyTiersOccupation.value.map(t => t.itemName))];
            uniqueItemNames.forEach((name) => {
              const itemData = gameItemsData[name];
              if (itemData) {
                itemDetails.value[name] = itemData;
              }
            });
          } else {
            weeklyTiersOccupation.value = [];
          }
          isLoadingRewards.value = false;
        }, (err) => {
          errorRewards.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลรางวัล: ' + err.message;
          isLoadingRewards.value = false;
        });
    };

    const openCharacterView = (docId) => {
      const routeUrl = router.resolve({ name: 'AdminCharacterView', params: { userId: docId } }).href;
      window.open(routeUrl, '_blank');
    };

    const rankBubbleClass = (index) => {
      if (index === 0) return 'bg-yellow-500 border-yellow-300';
      if (index === 1) return 'bg-gray-400 border-gray-200';
      if (index === 2) return 'bg-yellow-700 border-yellow-500';
      return 'bg-indigo-600 border-indigo-400';
    };

    const cardClass = (index, s) => {
      const cls = [];
      if (index === 0) cls.push('border-yellow-400');
      else if (index === 1) cls.push('border-gray-400');
      else if (index === 2) cls.push('border-yellow-600');
      else cls.push('border-gray-800');

      if (s && s.docId === userId.value) {
        cls.push('border-2', 'border-indigo-400', 'cursor-default');
      } else {
        cls.push('hover:border-indigo-400');
      }
      return cls;
    };

    // Rewards helpers
    const wearableTypes = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง',
      'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง',
      'ตะกรุด', 'ผ้ายันต์', 'สายสิญญ์'
    ];

    const timeLeft = ref('');
    const selectedItem = ref(null);

    const calculateTimeLeft = () => {
      // นับถอยหลังถึงวันอาทิตย์ 20:55 ตามเวลาท้องถิ่น
      const now = new Date();
      const dayOfWeek = now.getDay();
      let daysToNextSunday = (7 - dayOfWeek) % 7;
      const nextSunday = new Date(now);
      nextSunday.setDate(now.getDate() + daysToNextSunday);
      nextSunday.setHours(20, 55, 0, 0);
      let diff = nextSunday - now;
      if (diff <= 0) {
        nextSunday.setDate(nextSunday.getDate() + 7);
        diff = nextSunday - now;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      timeLeft.value = `${days} วัน ${hours} ชั่วโมง ${minutes} นาที ${seconds} วินาที`;
    };

    const showItemDetails = (itemName) => {
      if (itemDetails.value[itemName]) {
        selectedItem.value = itemName;
      }
    };

    const sortedTiers = computed(() => {
      return [...weeklyTiersOccupation.value].sort((a, b) => a.min - b.min).map(tier => ({
        ...tier,
        img: itemDetails.value[tier.itemName]?.img,
      }));
    });

    const currentItemDetails = computed(() => itemDetails.value[selectedItem.value] || {});
    const itemStats = computed(() => currentItemDetails.value.stats || {});
    const hasStats = computed(() => Object.values(itemStats.value).some(v => v > 0));

    // Mount / Unmount
    onMounted(async () => {
      await fetchUserId();
      listenToPlayers();
      listenToMaps();
      listenToRewards();
      calculateTimeLeft();
      setInterval(calculateTimeLeft, 1000);
    });

    onUnmounted(() => {
      if (playerListener) playerListener();
      if (mapListener) mapListener();
      if (rewardListener) rewardListener();
    });

    return {
      // tabs
      tabs, activeTab,

      // players
      occupiers, currentPlayer, currentPlayerRank, isLoadingPlayers, isLoadingMorePlayers,
      errorPlayers, userId, isAdmin, hasMorePlayers, loadMoreOccupiers,

      // maps
      rankedMaps, isLoadingMaps, errorMaps,

      // rewards
      weeklyTiersOccupation, isLoadingRewards, errorRewards, timeLeft, selectedItem,
      wearableTypes, sortedTiers, currentItemDetails, itemStats, hasStats, showItemDetails,

      // utils
      openCharacterView, handleImageError, fallbackImg,
      rankBubbleClass, cardClass
    };
  }
};
</script>

<style scoped>
#style-1::-webkit-scrollbar { width: 6px; }
#style-1::-webkit-scrollbar-track { background: #1f2937; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb { background: #374151; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb:hover { background: #4b5563; }

.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.text-xxs { font-size: 0.65rem; line-height: 0.85rem; }

.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2FGuestroom.png?alt=media&token=fc8f6e11-d047-4811-a36a-e7f225ef9d27') no-repeat center center fixed,
  linear-gradient(to bottom, rgba(31,41,55,0.8), rgba(0,0,0,0.9));
}

/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748; }

/* Animations */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }

@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }

@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }

/* Tooltip */
.tooltip {
  visibility: hidden;
  position: absolute;
  bottom: 100%; left: 50%; transform: translateX(-50%);
  background: #1f2937; color: #e0e7ff;
  padding: 0.5rem 1rem; border-radius: 0.375rem; font-size: 0.75rem;
  white-space: nowrap; z-index: 10; opacity: 0;
  transition: opacity 0.3s ease, visibility 0.3s ease; margin-bottom: 0.5rem;
}
button:hover .tooltip { visibility: visible; opacity: 1; }
</style>